import type { z } from 'zod';
import { ApiFailure, failureFromBody } from './errors';

/**
 * One thin wrapper over `fetch`, and every request in the app goes through it.
 *
 * Three responsibilities, none of them negotiable per call site:
 *
 *  1. **The credential is attached here.** `Authorization: Bearer <ID token>` on
 *     every request, from a `TokenSource` the caller supplies. On a `401` it
 *     forces exactly one token refresh and retries once — Firebase rotates ID
 *     tokens hourly and an iPad that was asleep will present a stale one on its
 *     first request back. One retry, not a loop: a second `401` means the
 *     session is genuinely over, and the outbox knows to wait rather than to
 *     reject (see `errors.ts`).
 *  2. **Every response is parsed** through a zod schema from `wire.ts` before it
 *     is returned. The mirror is what the device shows when there is no network
 *     to check against; a shape that drifted has to fail at the boundary, once,
 *     rather than as an undefined in a view an hour later.
 *  3. **`Idempotency-Key` is a first-class argument**, because the outbox's
 *     whole guarantee rests on it and a header you have to remember to set is a
 *     header somebody will not set.
 */

export type TokenSource = (forceRefresh?: boolean) => Promise<string | null>;

export interface RequestSpec<T> {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Under `/v1`, leading slash: `/appointments`. */
  path: string;
  query?: Record<string, string | number | undefined> | undefined;
  body?: unknown;
  idempotencyKey?: string | undefined;
  schema: z.ZodType<T>;
  signal?: AbortSignal | undefined;
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: TokenSource;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenSource;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.getToken = options.getToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  url(path: string, query?: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined) search.set(key, String(value));
    }
    const qs = search.toString();
    return `${this.baseUrl}/v1${path}${qs === '' ? '' : `?${qs}`}`;
  }

  async request<T>(spec: RequestSpec<T>): Promise<T> {
    const first = await this.send(spec, false);
    const response = first.status === 401 ? await this.send(spec, true) : first;

    const text = await response.text();
    const parsedJson = parseJson(text);

    if (!response.ok) {
      throw failureFromBody(response.status, parsedJson);
    }

    const parsed = spec.schema.safeParse(parsedJson);
    if (!parsed.success) {
      throw new ApiFailure('malformed', 'The server sent a shape this app does not know.', {
        status: response.status,
        detail: { issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) },
        body: parsedJson,
      });
    }
    return parsed.data;
  }

  /**
   * The event stream, as a raw `Response`.
   *
   * `EventSource` cannot carry an `Authorization` header — it has no API for
   * one — and `core-api` requires a Bearer token on every `/v1` route including
   * this one. So the stream is read with `fetch` and the frames are parsed by
   * hand (`mirror/sse.ts`). The consolation prize is real: `Last-Event-ID`
   * becomes an ordinary request header we control, so resuming from the stored
   * cursor is explicit rather than something the browser does for us on its own
   * schedule.
   */
  async stream(lastEventId: string | null, signal: AbortSignal): Promise<Response> {
    const token = await this.getToken();
    const headers: Record<string, string> = { accept: 'text/event-stream' };
    if (token !== null) headers['authorization'] = `Bearer ${token}`;
    if (lastEventId !== null) headers['Last-Event-ID'] = lastEventId;

    let response: Response;
    try {
      response = await this.fetchImpl(this.url('/events/stream'), { headers, signal });
    } catch (e) {
      throw ApiFailure.offline(String(e));
    }
    if (!response.ok) {
      throw failureFromBody(response.status, parseJson(await response.text()));
    }
    return response;
  }

  private async send<T>(spec: RequestSpec<T>, forceRefresh: boolean): Promise<Response> {
    const token = await this.getToken(forceRefresh);

    const headers: Record<string, string> = { accept: 'application/json' };
    if (token !== null) headers['authorization'] = `Bearer ${token}`;
    if (spec.body !== undefined) headers['content-type'] = 'application/json';
    if (spec.idempotencyKey !== undefined) headers['Idempotency-Key'] = spec.idempotencyKey;

    try {
      return await this.fetchImpl(this.url(spec.path, spec.query), {
        method: spec.method,
        headers,
        ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
        ...(spec.signal === undefined ? {} : { signal: spec.signal }),
      });
    } catch (e) {
      // `fetch` rejects for exactly one class of reason: the request did not
      // complete. Airplane mode, DNS, a dropped tunnel — from the outbox's point
      // of view they are one thing, and it is the retryable thing.
      throw ApiFailure.offline(String(e));
    }
  }
}

function parseJson(text: string): unknown {
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
